import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Search, ArrowRight, ShieldCheck, Sparkles, Users } from "lucide-react";
import { api } from "@/lib/api";
import { BusinessCard } from "@/components/BusinessCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const HERO_IMG =
  "https://images.unsplash.com/photo-1687197180710-b2b9484a3c5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsdWUlMjBjb3Jwb3JhdGUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MzQxMjMzOHww&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [f, c] = await Promise.all([
        api.get("/businesses", { params: { featured_only: true, limit: 8 } }),
        api.get("/categories"),
      ]);
      setFeatured(f.data.items);
      setCategories(c.data);
    })();
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (loc) params.set("city", loc);
    navigate(`/directory?${params.toString()}`);
  };

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-blue-950/70 to-slate-900/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28 md:pt-28 md:pb-36">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-3 py-1 text-xs uppercase tracking-widest text-white/90 mb-6" data-testid="hero-eyebrow">
              <ShieldCheck className="h-3.5 w-3.5" /> Trusted nationwide directory
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]" data-testid="hero-title">
              Find businesses and professionals America <span className="text-amber-300">actually trusts.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-200 max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              Verified reviews, premium listings, and a curated network of businesses and individual professionals across every state.
            </p>

            <form onSubmit={onSearch} className="mt-10 bg-white rounded-2xl shadow-2xl border border-white/10 p-3 flex flex-col md:flex-row gap-2" data-testid="hero-search-form">
              <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 md:border-0">
                <Search className="h-5 w-5 text-slate-400 shrink-0" />
                <Input
                  data-testid="hero-search-query"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Business, category, or professional…"
                  className="border-0 focus-visible:ring-0 shadow-none text-base placeholder:text-slate-400"
                />
              </div>
              <div className="hidden md:block w-px bg-slate-200" />
              <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 md:border-0">
                <Icons.MapPin className="h-5 w-5 text-slate-400 shrink-0" />
                <Input
                  data-testid="hero-search-location"
                  value={loc}
                  onChange={(e) => setLoc(e.target.value)}
                  placeholder="City (e.g. Austin, Miami)"
                  className="border-0 focus-visible:ring-0 shadow-none text-base placeholder:text-slate-400"
                />
              </div>
              <Button type="submit" size="lg" className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg h-12 px-8" data-testid="hero-search-submit">
                Search <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>

            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/85">
              <div className="flex items-center gap-2" data-testid="hero-stat-businesses"><Users className="h-4 w-4 text-amber-300" /> <span><span className="font-semibold text-white">50k+</span> verified businesses</span></div>
              <div className="flex items-center gap-2" data-testid="hero-stat-reviews"><Sparkles className="h-4 w-4 text-amber-300" /> <span><span className="font-semibold text-white">2M+</span> genuine reviews</span></div>
              <div className="flex items-center gap-2" data-testid="hero-stat-cities"><Icons.Map className="h-4 w-4 text-amber-300" /> <span><span className="font-semibold text-white">All 50</span> states covered</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="section-categories">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-700 font-medium mb-2">Browse by category</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Every trusted industry, one directory.</h2>
          </div>
          <Link to="/directory" className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900" data-testid="categories-view-all">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {categories.map((c) => {
            const Icon = Icons[c.icon] || Icons.Building2;
            return (
              <Link
                key={c.slug}
                to={`/directory?category=${c.slug}`}
                data-testid={`category-${c.slug}`}
                className="group bg-white rounded-xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <div className="h-11 w-11 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center mb-4 group-hover:bg-blue-700 group-hover:text-white transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="font-semibold text-slate-900">{c.name}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="section-featured">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-xs uppercase tracking-widest text-blue-700 font-medium mb-2">Premium listings</div>
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">Featured this month</h2>
            </div>
            <Link to="/directory?featured=1" className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900" data-testid="featured-view-all">
              See all featured <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((b) => (
              <BusinessCard key={b.id} business={b} />
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="section-trust">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-700 font-medium mb-3">Why DHD</div>
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-6">Built on verified trust — not paid rankings.</h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Every review is tied to a real, authenticated user. Every business can be claimed and verified. Premium listings are clearly marked so you always know what you're seeing.
            </p>
            <div className="grid grid-cols-2 gap-5">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="text-3xl font-bold text-slate-900">4.8</div>
                <div className="text-sm text-slate-500 mt-1">Avg. rating across listings</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="text-3xl font-bold text-slate-900">98%</div>
                <div className="text-sm text-slate-500 mt-1">Verified-owner response rate</div>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1573496130141-209d200cebd8?w=900"
                alt="Verified professional"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-xl shadow-lg p-4 max-w-xs">
              <div className="flex items-center gap-2 text-green-700 text-xs font-semibold mb-1">
                <ShieldCheck className="h-4 w-4" /> Verified Business
              </div>
              <div className="text-sm text-slate-700 leading-snug">
                "Domain Host Directory sends us clients who already trust us before the first call."
              </div>
              <div className="text-xs text-slate-500 mt-2">— Sarah C., Realtor · Compass</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
